/**
 * bucketPlan — Pure helpers for the Bucket List ladder (Save → Plan → Do).
 * A Bucket List item is an experience, not a task: no "due", no "overdue".
 * UAE weekend = Saturday + Sunday.
 */
import { addDays, differenceInCalendarDays, format, isValid, parseISO, startOfDay } from "date-fns";
import { BUCKET_LIST_CATEGORY } from "@/constants/categories";
import type { Goal } from "@/types/todo";

export type BucketState = "want" | "planned" | "missed" | "done";

/** How many days after a planned date we keep gently asking "Did you go?". */
export const DID_YOU_GO_WINDOW_DAYS = 7;

export const isBucketItem = (g: Pick<Goal, "category">) => g.category === BUCKET_LIST_CATEGORY;

export const isBucketDone = (g: Goal) => !!g.completedAt || (g.progress ?? 0) >= 100;

export function planDate(g: Goal): Date | null {
  if (!g.dueDate) return null;
  const d = typeof g.dueDate === "string" ? parseISO(g.dueDate) : (g.dueDate as unknown as Date);
  return isValid(d) ? startOfDay(d) : null;
}

export function getBucketState(g: Goal, today: Date = new Date()): BucketState {
  if (isBucketDone(g)) return "done";
  const d = planDate(g);
  if (!d) return "want";
  return differenceInCalendarDays(d, today) < 0 ? "missed" : "planned";
}

/** Saturday/Sunday of the current (or upcoming) UAE weekend. */
export function upcomingWeekend(today: Date = new Date()): { start: Date; end: Date } {
  const t = startOfDay(today);
  const dow = t.getDay(); // 0 Sun … 6 Sat
  if (dow === 0) return { start: addDays(t, -1), end: t };
  const start = addDays(t, 6 - dow);
  return { start, end: addDays(start, 1) };
}

export function isThisWeekend(d: Date, today: Date = new Date()): boolean {
  const { start, end } = upcomingWeekend(today);
  const t = startOfDay(today);
  return d >= (start < t ? t : start) && d <= end;
}

/** Friendly plan label: "Today", "Tomorrow", "This Saturday", "Oct 24". */
export function planLabel(d: Date, today: Date = new Date()): string {
  const diff = differenceInCalendarDays(d, today);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff > 1 && diff < 7) return `This ${format(d, "EEEE")}`;
  return format(d, "MMM d");
}

/** Planned items in the next weekend window, soonest first. */
export function weekendPlans(goals: Goal[], today: Date = new Date()): Goal[] {
  return goals
    .filter((g) => isBucketItem(g) && !g.isExample && getBucketState(g, today) === "planned")
    .filter((g) => isThisWeekend(planDate(g)!, today))
    .sort((a, b) => planDate(a)!.getTime() - planDate(b)!.getTime());
}

/** Planned dates that passed recently → gentle "Did you go?". */
export function didYouGoCandidates(goals: Goal[], today: Date = new Date()): Goal[] {
  return goals
    .filter((g) => isBucketItem(g) && !g.isExample && getBucketState(g, today) === "missed")
    .filter((g) => differenceInCalendarDays(today, planDate(g)!) <= DID_YOU_GO_WINDOW_DAYS)
    .sort((a, b) => planDate(b)!.getTime() - planDate(a)!.getTime());
}
